import { useState, useEffect, useCallback } from 'react';
import { MapPin, Plus, Trash2, Star, Phone, Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useLocations } from '../../context/LocationContext';

/**
 * Per-location settings tab. CRUD + Buy a number CTA + promote primary.
 * Surfaces the `is_legacy_pricing` plan-cap honestly so customers know
 * exactly how many slots they have left.
 */
export default function LocationsTab({ api }) {
  const { locations, primaryId, maxLocations, refresh, isMultiLocation } = useLocations();
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ name: '', city: '', address: '', timezone: '' });
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState('');
  const [newLoc, setNewLoc] = useState({ name: '', city: '', address: '', timezone: '' });

  const startEdit = (loc) => {
    setEditingId(loc.id);
    setDraft({
      name: loc.name || '',
      city: loc.city || '',
      address: loc.address || '',
      timezone: loc.timezone || '',
    });
  };
  const cancelEdit = () => { setEditingId(null); setDraft({ name: '', city: '', address: '', timezone: '' }); };

  const save = async (locId) => {
    setBusy(locId);
    try {
      await api.put(`/locations/${locId}`, draft);
      await refresh();
      cancelEdit();
      toast.success('Location updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save.');
    } finally { setBusy(''); }
  };

  const remove = async (locId, name) => {
    if (!window.confirm(`Delete location "${name}"? This can't be undone.`)) return;
    setBusy(locId);
    try {
      await api.delete(`/locations/${locId}`);
      await refresh();
      toast.success('Location removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete.');
    } finally { setBusy(''); }
  };

  const promote = async (locId) => {
    setBusy(locId);
    try {
      await api.post(`/locations/${locId}/promote`);
      await refresh();
      toast.success('Primary location updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not promote.');
    } finally { setBusy(''); }
  };

  const buyNumber = async (locId) => {
    setBusy(`buy-${locId}`);
    try {
      const { data } = await api.post(`/locations/${locId}/buy-number`);
      await refresh();
      toast.success(`Number purchased: ${data.phone_number}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not buy a number right now.');
    } finally { setBusy(''); }
  };

  const handleAdd = async () => {
    if (!newLoc.name.trim()) { toast.error('Name is required.'); return; }
    setBusy('add');
    try {
      await api.post('/locations', newLoc);
      await refresh();
      setNewLoc({ name: '', city: '', address: '', timezone: '' });
      setAdding(false);
      toast.success('Location added');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add location.');
    } finally { setBusy(''); }
  };

  const canAdd = locations.length < maxLocations;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="locations-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-xl font-semibold text-gray-900 dark:text-white">Locations</h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
            Each location gets its own Ava number and shows up in the dashboard switcher.
            You're using <strong>{locations.length}</strong> of <strong>{maxLocations}</strong> {maxLocations === 1 ? 'location' : 'locations'} on your plan.
          </p>
        </div>
        {!adding && canAdd && (
          <button
            data-testid="locations-add-btn"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white"
          >
            <Plus className="w-4 h-4" /> Add location
          </button>
        )}
        {!canAdd && (
          <span className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 px-3 py-1 rounded-full">
            Plan limit reached. Upgrade to add more.
          </span>
        )}
      </div>

      {adding && (
        <div data-testid="locations-add-form" className="rounded-2xl border border-blue-200 dark:border-blue-500/30 bg-blue-50/50 dark:bg-blue-500/[0.04] p-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Name *" value={newLoc.name} onChange={(v) => setNewLoc({ ...newLoc, name: v })} testid="locations-add-name" placeholder="Brooklyn studio" />
            <Field label="City" value={newLoc.city} onChange={(v) => setNewLoc({ ...newLoc, city: v })} testid="locations-add-city" placeholder="Brooklyn" />
            <Field label="Address" value={newLoc.address} onChange={(v) => setNewLoc({ ...newLoc, address: v })} testid="locations-add-address" placeholder="123 Main St, Brooklyn, NY" />
            <Field label="Timezone" value={newLoc.timezone} onChange={(v) => setNewLoc({ ...newLoc, timezone: v })} testid="locations-add-tz" placeholder="America/New_York" />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setAdding(false); setNewLoc({ name: '', city: '', address: '', timezone: '' }); }} className="text-sm px-3 py-1.5 rounded-full text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-white/[0.05]">Cancel</button>
            <button data-testid="locations-add-save" onClick={handleAdd} disabled={busy === 'add'} className="text-sm px-3 py-1.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-60 inline-flex items-center gap-1">
              {busy === 'add' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {locations.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-slate-400 italic py-6 text-center">No locations yet.</div>
        ) : locations.map((loc) => {
          const isEditing = editingId === loc.id;
          const isPrimary = loc.id === primaryId || loc.is_primary;
          return (
            <div key={loc.id} data-testid={`location-card-${loc.id}`} className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/[0.03] p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <div className="grid sm:grid-cols-2 gap-2">
                      <Field label="Name" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} testid={`location-edit-name-${loc.id}`} />
                      <Field label="City" value={draft.city} onChange={(v) => setDraft({ ...draft, city: v })} testid={`location-edit-city-${loc.id}`} />
                      <Field label="Address" value={draft.address} onChange={(v) => setDraft({ ...draft, address: v })} testid={`location-edit-address-${loc.id}`} />
                      <Field label="Timezone" value={draft.timezone} onChange={(v) => setDraft({ ...draft, timezone: v })} testid={`location-edit-tz-${loc.id}`} />
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">{loc.name}</h3>
                        {isPrimary && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-full">
                            <Star className="w-2.5 h-2.5" /> Primary
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-slate-400 mt-0.5 space-y-0.5">
                        {loc.address && <div>{loc.address}</div>}
                        {loc.city && !loc.address?.includes(loc.city) && <div>{loc.city}</div>}
                        {loc.timezone && <div className="text-[11px]">{loc.timezone}</div>}
                      </div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        {loc.twilio_number ? (
                          <span data-testid={`location-number-${loc.id}`} className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300">
                            <Phone className="w-3 h-3" /> {loc.twilio_number}
                          </span>
                        ) : (
                          <button
                            data-testid={`location-buy-number-${loc.id}`}
                            onClick={() => buyNumber(loc.id)}
                            disabled={busy === `buy-${loc.id}`}
                            className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-500/15 disabled:opacity-60"
                          >
                            {busy === `buy-${loc.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Phone className="w-3 h-3" />} Buy a number
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {isEditing ? (
                    <>
                      <button data-testid={`location-save-${loc.id}`} onClick={() => save(loc.id)} disabled={busy === loc.id} className="p-2 rounded-md text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 disabled:opacity-60">
                        {busy === loc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      </button>
                      <button onClick={cancelEdit} className="p-2 rounded-md text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-white/[0.05]">
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button data-testid={`location-edit-${loc.id}`} onClick={() => startEdit(loc)} className="text-xs font-medium px-2.5 py-1 rounded-md text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-white/[0.05]">
                        Edit
                      </button>
                      {!isPrimary && isMultiLocation && (
                        <button data-testid={`location-promote-${loc.id}`} onClick={() => promote(loc.id)} disabled={busy === loc.id} className="text-xs font-medium px-2.5 py-1 rounded-md text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 disabled:opacity-60">
                          Make primary
                        </button>
                      )}
                      {!isPrimary && (
                        <button data-testid={`location-delete-${loc.id}`} onClick={() => remove(loc.id, loc.name)} disabled={busy === loc.id} className="p-2 rounded-md text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 disabled:opacity-60">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-slate-500">{label}</span>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full text-sm rounded-md border border-gray-200 dark:border-white/10 bg-white dark:bg-[#0a0c10] px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-900 dark:text-white"
      />
    </label>
  );
}
